import * as React from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { useAppSelector } from '../../../app/hooks'
import { useLoadLeagueData } from '../../../app/dataLoaderHooks';
import { League } from '../../../sdk/model';
import {
  Box, 
  Dialog, 
  DialogContent, 
  Typography, 
  IconButton, 
  Avatar, 
  Skeleton,
  Chip
} from '@mui/material';
import CloseIcon from "@mui/icons-material/Close";
import ImageIcon from '@mui/icons-material/Image';

function LeagueName() {
  const { leagueId } = useParams();
  const location = useLocation();
  const user = useAppSelector((state) => state.user);

  const [viewingImage, setViewingImage] = React.useState(false);
  const [isLoadComplete, setLoadComplete] = React.useState(false);
  const [isException, setIsException] = React.useState(false);
  const [hasAdvertisement, setHasAdvertisement] = React.useState(false);

  const league: League | undefined = useLoadLeagueData({
    leagueId: leagueId,
    userId: user.id,
    loadBasicDraftData: false,
    loadFullDraftData: false,
    loadContractData: false,
    loadBasicAuctionData: false,
    loadFullAuctionData: false,
    loadRosterData: false,
    loadRosterFor: [],
    loadWaivedContracts: false,
    loadScheduleData: false,
    isException: isException,
    setIsException: setIsException,
    isLoadComplete: isLoadComplete,
    setLoadComplete: setLoadComplete
  });

  // Check if advertisement image exists
  React.useEffect(() => {
    const checkAdvertisementImage = () => {
      const img = new Image();
      
      img.onload = () => {
        setHasAdvertisement(true);
      };
      
      img.onerror = () => {
        setHasAdvertisement(false);
      };
      
      // Start loading the image
      img.src = '/assets/image/advertisement.png';
    };
    
    checkAdvertisementImage();
  }, []);

  // Calculate established year safely
  const establishedYear = React.useMemo(() => {
    if (!league?.currentSeasonYear || !league?.totalSeasons) {
      return null;
    }
    return league.currentSeasonYear - league.totalSeasons + 1;
  }, [league?.currentSeasonYear, league?.totalSeasons]);

  // Handle image dialog
  const handleCloseImage = () => setViewingImage(false);
  const handleOpenImage = () => league?.picture && setViewingImage(true);

  // Show loading state
  if (!isLoadComplete && !isException) {
    return (
      <Box sx={{ py: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
          <Skeleton variant="circular" width={40} height={40} />
          <Skeleton variant="text" width={200} height={40} />
        </Box>
        <Skeleton variant="text" width={120} height={24} />
      </Box>
    );
  }

  // Show error state
  if (isException || !league) {
    return (
      <Box sx={{ py: 2 }}>
        <Typography variant="h6" color="error">
          Error loading league information
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ textAlign: 'center', py: 2 }}>
      {/* Image Viewing Dialog */}
      <Dialog 
        open={viewingImage} 
        onClose={handleCloseImage}
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: { bgcolor: 'background.paper' }
        }}
      >
        <DialogContent sx={{ p: 0, position: 'relative' }}>
          <IconButton
            onClick={handleCloseImage}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              bgcolor: 'background.paper',
              zIndex: 1,
              '&:hover': { bgcolor: 'background.default' }
            }}
          >
            <CloseIcon />
          </IconButton>
          <Box
            component="img"
            src={league.picture}
            alt={league.leagueName || 'League Image'}
            sx={{
              width: '100%',
              height: 'auto',
              maxHeight: '80vh',
              objectFit: 'contain',
              display: 'block'
            }}
          />
        </DialogContent>
      </Dialog>

      {/* League Name and Avatar */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 1 }}>
        <IconButton 
          onClick={handleOpenImage}
          disabled={!league.picture}
          sx={{ 
            p: 0,
            '&:hover': league.picture ? { opacity: 0.8 } : undefined,
            cursor: league.picture ? 'pointer' : 'default'
          }}
        >
          <Avatar 
            alt={league.leagueName || 'League'} 
            src={league.picture}
            sx={{ 
              width: 40, 
              height: 40,
              bgcolor: 'primary.main',
              fontSize: '1.2rem',
              fontWeight: 'bold'
            }}
          >
            {!league.picture && (league.leagueName?.charAt(0)?.toUpperCase() || <ImageIcon />)}
          </Avatar>
        </IconButton>
        
        <Typography 
          variant="h4" 
          component="h1" 
          color="text.secondary"
          sx={{ 
            fontWeight: 600,
            fontSize: { xs: '1.5rem', sm: '2rem' },
            lineHeight: 1.2
          }}
        >
          {league.leagueName || 'Unknown League'}
        </Typography>
      </Box>

      {/* Established Date with Dynasty Badge */}
      {establishedYear && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
          <Typography 
            variant="body1" 
            color="text.secondary"
            sx={{ fontSize: '1.25rem' }}
          >
            Est. {establishedYear}
          </Typography>
          {league.totalSeasons && league.totalSeasons >= 3 && (
            <Chip
              label="Dynasty"
              size="small"
              color="primary"
              sx={{ 
                fontSize: '0.75rem',
                fontWeight: 600,
                ml: 1
              }}
            />
          )}
        </Box>
      )}

      {/* Advertisement Space - Only show on league home page */}
      {hasAdvertisement && location.pathname === `/league/${leagueId}` && (
        <Box sx={{ 
          mt: 2, 
          display: 'flex', 
          justifyContent: 'center',
          maxWidth: '100%'
        }}>
          <Box
            component="img"
            src="/assets/image/advertisement.png"
            alt="Advertisement"
            sx={{
              width: '1000px',
              maxWidth: '100%',
              maxHeight: '300px',
              objectFit: 'stretch',
              borderRadius: 1,
              boxShadow: 1
            }}
          />
        </Box>
      )}
    </Box>
  );
}

export default LeagueName;
